import { useEffect, useMemo, useState } from 'react';
import {
  EventBus, DEFAULT_NOTICE_TITLE, NOTICE_LABEL_MAX, NOTICE_NOTE_MAX, NOTICE_TITLE_MAX,
  type NoticeBoardContent, type NoticeDuo,
} from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { apiHeaders, loadMembers, sessionInfo, useMembersStore } from '../people/membersStore.js';
import { PersonAvatar } from '../people/PersonAvatar.js';
import { Button, Input, Modal, Select, Textarea } from '../ds/index.js';

interface Loaded { content: NoticeBoardContent | null; updatedAt: string | null; canEdit: boolean }

const ERRORS: Record<string, string> = {
  INVALID_DUO: 'Chaque duo doit avoir deux personnes différentes (trois au plus), et une personne n’est que dans un duo.',
  UNKNOWN_MEMBER: 'Une des personnes choisies n’est plus membre du campus.',
  TOO_MANY_DUOS: 'Trop de duos : 200 au plus.',
  FORBIDDEN: 'Seuls les admins peuvent modifier ce tableau.',
};

const day = (iso: string) => new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'long' }).format(new Date(iso));

/**
 * « Lire le tableau » d'un panneau d'affichage (étage Duos) : la liste des duos
 * du dernier tirage, son propre duo en premier. Les admins la remplissent après
 * chaque tirage au sort (« Modifier »).
 */
export function NoticeBoardModal() {
  const [itemId, setItemId] = useState<string | null>(null);
  const [data, setData] = useState<Loaded | null>(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const open = ({ itemId: id }: { itemId: string }) => { setItemId(id); setEditing(false); };
    EventBus.on('ui:notice-board-open', open);
    return () => { EventBus.off('ui:notice-board-open', open); };
  }, []);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    setData(null);
    setFailed(false);
    if (!sessionInfo().isGuest) void loadMembers();
    fetch(`${API_BASE}/world/notice-boards/${itemId}`, { headers: apiHeaders() })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json() as Promise<Loaded>; })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [itemId]);

  if (!itemId) return null;
  const close = () => { setItemId(null); setData(null); };
  const title = data?.content?.title || DEFAULT_NOTICE_TITLE;

  if (editing && data) {
    return (
      <NoticeBoardEditor
        itemId={itemId}
        initial={data.content}
        onCancel={() => setEditing(false)}
        onSaved={(d) => { setData(d); setEditing(false); }}
      />
    );
  }

  return (
    <Modal title={title} onClose={close} footer={data?.canEdit ? (
      <>
        <Button variant="secondary" onClick={close}>Fermer</Button>
        <Button variant="primary" icon="edit" onClick={() => setEditing(true)}>Modifier</Button>
      </>
    ) : undefined}>
      {failed && <p className="pl-help">Impossible de lire ce tableau. Réessaie dans un instant.</p>}
      {!failed && !data && <p className="pl-help">Chargement…</p>}
      {data && <NoticeBoardView content={data.content} updatedAt={data.updatedAt} canEdit={data.canEdit} />}
    </Modal>
  );
}

function NoticeBoardView({ content, updatedAt, canEdit }: { content: NoticeBoardContent | null; updatedAt: string | null; canEdit: boolean }) {
  const members = useMembersStore((s) => s.members);
  const me = sessionInfo().userId;
  const duos = content?.duos ?? [];
  // Son propre duo en premier.
  const ordered = useMemo(() => {
    const mine = duos.findIndex((d) => d.userIds.includes(me));
    return mine > 0 ? [duos[mine]!, ...duos.filter((_, i) => i !== mine)] : duos;
  }, [duos, me]);
  const name = (id: string) => (id === me ? 'Toi' : members.get(id)?.displayName ?? 'Ancien membre');

  if (duos.length === 0) {
    return (
      <p className="pl-help" style={{ marginTop: 0 }}>
        {canEdit ? 'Aucun duo affiché. « Modifier » pour saisir le résultat du tirage.' : 'Les duos ne sont pas encore affichés. Reviens après le tirage au sort.'}
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {content?.note && <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 'var(--text-sm)' }}>{content.note}</p>}
      {updatedAt && <p className="pl-help" style={{ margin: 0 }}>{duos.length} duo{duos.length > 1 ? 's' : ''} · mis à jour le {day(updatedAt)}</p>}
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
        {ordered.map((d) => {
          const mine = d.userIds.includes(me);
          return (
            <li key={d.userIds.join('+')} className="pl-card" style={{
              padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6,
              ...(mine ? { borderColor: 'var(--color-primary)', boxShadow: '0 0 0 1px var(--color-primary)' } : {}),
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {mine ? 'Ton duo' : d.label || `Duo ${duos.indexOf(d) + 1}`}{mine && d.label ? ` · ${d.label}` : ''}
              </span>
              {d.userIds.map((u) => (
                <span key={u} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <PersonAvatar userId={u} name={members.get(u)?.displayName ?? '?'} size={28} highlight={u === me} />
                  <span style={{ fontWeight: u === me ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name(u)}</span>
                </span>
              ))}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Saisie du tirage (admins) : titre, mot d'accompagnement, duos choisis dans la liste des membres. */
function NoticeBoardEditor({ itemId, initial, onCancel, onSaved }: {
  itemId: string;
  initial: NoticeBoardContent | null;
  onCancel: () => void;
  onSaved: (d: Loaded) => void;
}) {
  const members = useMembersStore((s) => s.members);
  const [title, setTitle] = useState(initial?.title ?? DEFAULT_NOTICE_TITLE);
  const [note, setNote] = useState(initial?.note ?? '');
  const [duos, setDuos] = useState<NoticeDuo[]>(() => initial?.duos.map((d) => ({ ...d, userIds: [...d.userIds] })) ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Membres simples d'abord (ceux qui sont tirés au sort), par ordre alphabétique.
  const people = useMemo(() => [...members.values()]
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr')), [members]);
  const taken = new Map<string, number>();
  duos.forEach((d, i) => d.userIds.forEach((u) => { if (u) taken.set(u, i); }));

  const options = (duoIndex: number, current: string) => [
    { value: '', label: 'Choisir une personne' },
    ...people
      .filter((p) => p.userId === current || !taken.has(p.userId) || taken.get(p.userId) === duoIndex)
      .map((p) => ({ value: p.userId, label: p.displayName })),
  ];

  const update = (i: number, fn: (d: NoticeDuo) => NoticeDuo) => setDuos((list) => list.map((d, j) => (j === i ? fn(d) : d)));
  const complete = duos.filter((d) => d.userIds.filter(Boolean).length >= 2);
  const incomplete = duos.length - complete.length;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        title, note,
        duos: duos
          .map((d) => ({ ...d, userIds: d.userIds.filter(Boolean) }))
          .filter((d) => d.userIds.length > 0),
      };
      const res = await fetch(`${API_BASE}/world/notice-boards/${itemId}`, { method: 'PUT', headers: apiHeaders(true), body: JSON.stringify(body) });
      if (!res.ok) {
        const { error: code } = await res.json().catch(() => ({} as { error?: string })) as { error?: string };
        setError(ERRORS[code ?? ''] ?? 'L’enregistrement n’a pas marché. Réessaie.');
        return;
      }
      onSaved(await res.json() as Loaded);
      EventBus.emit('ui:toast', { message: 'Tableau des duos mis à jour.', tone: 'success' });
    } catch {
      setError('Erreur réseau. Réessaie.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Modifier le tableau des duos" size="lg" onClose={onCancel} footer={
      <>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Annuler</Button>
        <Button variant="primary" onClick={() => void save()} disabled={busy || incomplete > 0}>
          {busy ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </>
    }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input label="Titre" value={title} maxLength={NOTICE_TITLE_MAX} onChange={(e) => setTitle(e.target.value)} />
        <Textarea label="Mot d'accompagnement (facultatif)" value={note} maxLength={NOTICE_NOTE_MAX} rows={2}
          placeholder="Ex. Tirage du 3 novembre : revendiquez votre salon à deux dès lundi."
          onChange={(e) => setNote(e.target.value)} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong style={{ flex: 1 }}>{duos.length} duo{duos.length > 1 ? 's' : ''}</strong>
          {duos.length > 0 && <Button variant="ghost" size="sm" icon="trash" onClick={() => setDuos([])}>Vider la liste</Button>}
        </div>
        {duos.length === 0 && <p className="pl-help" style={{ margin: 0 }}>Ajoute un duo par binôme tiré au sort. Une personne ne peut être que dans un duo.</p>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {duos.map((d, i) => (
            <div key={i} className="pl-card" style={{ padding: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 8 }}>
              <Input label={`Duo ${i + 1}`} placeholder="Nom (facultatif)" value={d.label ?? ''} maxLength={NOTICE_LABEL_MAX} style={{ width: 140 }}
                onChange={(e) => update(i, (x) => ({ ...x, label: e.target.value }))} />
              {d.userIds.map((u, k) => (
                <Select key={k} size="sm" aria-label={`Personne ${k + 1} du duo ${i + 1}`} value={u} options={options(i, u)} style={{ minWidth: 170 }}
                  onChange={(e) => update(i, (x) => ({ ...x, userIds: x.userIds.map((v, n) => (n === k ? e.target.value : v)) }))} />
              ))}
              {d.userIds.length < 3 ? (
                <Button variant="ghost" size="sm" icon="user-plus" onClick={() => update(i, (x) => ({ ...x, userIds: [...x.userIds, ''] }))}>Trio</Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => update(i, (x) => ({ ...x, userIds: x.userIds.slice(0, 2) }))}>Remettre à deux</Button>
              )}
              <Button variant="ghost" size="sm" icon="trash" aria-label={`Retirer le duo ${i + 1}`} onClick={() => setDuos((list) => list.filter((_, j) => j !== i))} />
            </div>
          ))}
        </div>
        <div>
          <Button variant="secondary" icon="plus" onClick={() => setDuos((list) => [...list, { userIds: ['', ''] }])}>Ajouter un duo</Button>
        </div>
        {incomplete > 0 && <p className="pl-help" style={{ margin: 0 }}>Choisis deux personnes dans chaque duo ({incomplete} incomplet{incomplete > 1 ? 's' : ''}), ou retire-le.</p>}
        {error && <p className="pl-help" style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p>}
      </div>
    </Modal>
  );
}
